import F from 'epicenter-js'
import data from './data'

const { runManagerAccount, outputs, runStages, scenarios, zones } = data

const rm = new F.manager.RunManager(runManagerAccount)

/**
 * Inputs are stored in the `[inputs]` variables. To apply them to
 * this Forio run, we have to save it with the variable service.
 */
const applyInputs = async function (Run) {
  console.log('- - - applyInputs - - -')

  if (!Run.getRunService()) {
    await initForioRun(Run)
    await initWithScenario(Run)
  }

  const currentStep = Run.getCurrentStep()
  const previousStep = currentStep - 1

  const variables = Run.getInputVariables()
  for (let v of variables) {
    const oldValue = v.values['time' + previousStep] ? 1 : 0
    const newValue = v.values['time' + currentStep] ? 1 : 0
    if (oldValue === newValue) {
      console.log(`- - - applyInput: ${v.modelVariable} | unchanged - - -`)
    } else {
      console.log(`- - - applyInput: ${v.modelVariable} | ${newValue} - - -`)
      await Run.getVariableService().save(v.modelVariable, newValue)
    }
  }
}

/**
 * Once the variable service is ready, we can query the
 * latest output using this function.
 */
const queryOutput = async function (Run) {
  // Get list of all output variables
  const allOutputVariables = []
  for (let output of outputs) {
    allOutputVariables.push(output.name)
  }

  // Then fetch the results for these variables
  const response = await Run.getVariableService().query(allOutputVariables)

  return response
}

/**
 * In the current Forio run, do one new iteration. (If it
 * runs quarterly, this means that we want to compute the
 * output for the next quarter.)
 *
 * @param {*} callback
 */
const doSteps = async function ({ Run, numberOfSteps = 1, maxRetries = 0 }) {
  const currentStep = Run.getCurrentStep()
  Run.setRunning(true)

  console.log(`- - - doSteps (${numberOfSteps}) - - -`)

  if (!Run.getRunService()) {
    await initForioRun(Run)
    await initWithScenario(Run)
  }

  // First apply the inputs that might have changed,
  // and only then calculate the next step of the model
  await applyInputs(Run)

  const stepTo = runStages[currentStep - 1 + numberOfSteps].stepTo

  console.log(`- - - do.stepTo=${stepTo} - - -`)
  try {
    await Run.getRunService().do('stepTo', stepTo)
    const response = await queryOutput(Run)

    // Remove the nodes we don't use so that we can handle smaller
    // JSONs in the browser
    for (let k1 of Object.keys(response)) {
      for (let k2 of Object.keys(response[k1])) {
        if (!Number.isInteger(parseInt(k2))) {
          let found = false
          for (let zone of zones) {
            if (k2 === zone.name) {
              found = true
            }
          }
          if (!found) {
            delete response[k1][k2]
          }
        }
      }
    }

    // Run this without changing any of the outputs, and you'll get
    // our baseline here:
    // console.log(JSON.stringify(response))

    Run.setCurrentStep(currentStep + numberOfSteps)
    Run.setSavedOutput(response)
    Run.setRunning(false)
  } catch (error) {
    if (maxRetries && maxRetries > 1) {
      console.log(`- - - Error. Retrying... - - -`)
      console.log(error)
      return await doSteps({
        Run,
        numberOfSteps,
        maxRetries: maxRetries - 1,
      })
    } else {
      console.log(`- - - Error. Stopping here. - - -`)
      return Run.setRunning(false)
    }
  }

  Run.setRunning(false)
}

const initWithScenario = async function (Run) {
  // The initial run hasn't started yet, we need to start
  // game world on Forio first.
  await Run.getRunService().do('startGame')
  console.log(`- - - startGame - - -`)

  // Once the Run is going, we need to apply the first variable,
  // which determines the scenario
  if (Run.getScenario()) {
    const variable = Run.getScenario().variable

    // First we need to switch off all scenarios, and then enable to
    // one and only that we use
    let scenarioToEnable = null
    for (let scenario of scenarios) {
      if (scenario.variable.name === variable.name) {
        scenarioToEnable = scenario
      } else {
        console.log(`- - - applyScenario: ${scenario.variable.name} (0) - - -`)
        await Run.getVariableService().save(variable.name, 0)
      }
    }
    console.log(
      `- - - applyScenario: ${scenarioToEnable.variable.name} (1) - - -`,
    )
    await Run.getVariableService().save(scenarioToEnable.variable.name, 1)
  }

  // Now that we're all set, let's start with the steps
  Run.setCurrentStep(0)
}

/**
 * Create the Forio Run, then store the run service and
 * variable service variables for later use.
 */
const initForioRun = async function (Run) {
  console.log('- - - initForioRun - - -')

  const run = await rm.getRun()
  console.log(`Run started with RunID: ${run.id}`)

  Run.setRunService(rm.run)
  Run.setVariableService(rm.run.variables())
}

export default {
  doSteps,
}
